import {
  NavbarDropdownMenuColumnElement,
  NavbarDropdownMenuElement,
  NavbarDropdownMenuItemElement,
  NavbarMenuItemProps,
} from '@typedream/components';
import {
  NavbarDropdown, NavbarDropdownMenu, NavbarElement,
} from '@typedream/core';
import { isElementEmpty } from '@udecode/plate-core';
import React, {
  useContext, useEffect, useRef,
} from 'react';
import styled from 'styled-components';
import { useTPlateEditorRef } from '../../../plate';
import { NavbarMenuItemContext } from '../context/navbar.context';
import { NavbarDropdownSelectedItemProvider } from '../context/navbarDropdownSelectedItem.context';
import { DropdownElementBox } from './DropdownMenu/DropdownElementBox';
import { EmptyColumnPlaceholder } from './DropdownMenu/EmptyColumnPlaceholder';

// ======================================================
// STYLED COMPONENT
// ======================================================
const NavbarEditorWrapper = styled.div<{ show?: boolean }>`
  visbiility: ${({ show }) => (show ? 'visible' : 'hidden')};
  width: 100%;
  cursor: default;
  position: absolute;
  left: 0px;
  top: 61px;
  background-color: var(--background);
  z-index: 99;
  height: 83px;

  @media only screen and (min-width: 1024px) {
    position: absolute;
    left: 0px;
    top: 61px;
    z-index: 99;
  
    :global(.typedream.content) {
      overflow: visible;
    }
  }
`;

// This element also replicate `slate-react`'s `Editable` styles
const NavbarDropdownMenuWrapper = styled.div`
  width: 100%;
  cursor: default;

  position: relative; 
  outline: none; 
  white-space: pre-wrap; 
  overflow-wrap: break-word; 
  background-color: transparent;
`;

type NavbarDropdownMenuEditorProps = {
  show?: boolean;
} & NavbarMenuItemProps<NavbarDropdown>;

function NavbarDropdownMenuEditor(props: NavbarDropdownMenuEditorProps) {
  const {
    item, show, endMenu, index,
  } = props;
  const { element: navbar } = useContext(NavbarMenuItemContext);
  const dropdownMenus: NavbarDropdownMenu = item?.menu;
  const dropdownMenuRef = useRef<HTMLDivElement>(null);
  const editor = useTPlateEditorRef();

  useEffect(() => {
    if (show && dropdownMenuRef?.current) {
      editor.emitter.emit(
        'toolbar:navbar:dropdown:menu:open',
        {
          ref: dropdownMenuRef?.current,
          state: true,
          nav: navbar,
          menuIndex: index,
          type: endMenu ? 'endMenu' : 'menu',
        },
      );
    }
  }, [show]);

  // In the original `renderElement`, the accepted props are `RenderElementProps`.
  // which retrieved from Slate's `Editable` component. Then, in the implementation detail,
  // typecast the element to `NavbarElement`. In this method, we used `NavbarElement`
  // directly.
  const renderElement = (el: NavbarElement, parentRef?: {
    columnId?: string;
    columnItemId?: string;
  }) => {
    switch (el.type) {
      case 'navbar_dropdown_menu':
        return (
          <NavbarDropdownMenuElement element={el} className={`slate-${el.type}`}>
            {/* Direct children are NavbarItem and NavbarColumn */}
            {el.children.map((child) => renderElement(child as NavbarElement))}
          </NavbarDropdownMenuElement>
        );

      case 'navbar_dropdown_menu_column': {
        if (
          el.children.length === 0
          || (
            el.children.length === 1
            && isElementEmpty(editor, el))
        ) {
          return (
            <DropdownElementBox
              element={el}
              menuIndex={index}
              endMenu={endMenu ?? false}
              columnId={el.id}
            >
              <EmptyColumnPlaceholder
                element={el}
                onAddMenuItem={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  editor.emitter.emit(
                    'toolbar:navbar:dropdown-empty-menu-column:add',
                    {
                      nav: navbar,
                      navbarMenuItemIndex: index,
                      navbarMenuItemType: endMenu ? 'endMenu' : 'menu',
                      columnId: el.id,
                    },
                  );
                }}
              />
            </DropdownElementBox>
          );
        }
        const { children, ...restEl } = el;
        return (
          <DropdownElementBox
            element={el}
            menuIndex={index}
            endMenu={endMenu ?? false}
            columnId={el.id}
          >
            <NavbarDropdownMenuColumnElement
              {...restEl}
              element={el}
              className={`slate-${el.type}`}
            >
              {children.map((child) => renderElement(child as NavbarElement, { columnId: el.id }))}
            </NavbarDropdownMenuColumnElement>
          </DropdownElementBox>
        );
      }

      case 'navbar_dropdown_menu_item': {
        // URL removed to prevent link clickable in editor
        const { url, ...elWithoutLink } = el;
        return (
          <DropdownElementBox
            element={el}
            menuIndex={index}
            endMenu={endMenu ?? false}
            columnItemId={el.id}
            columnId={parentRef?.columnId}
            styles={{
              gridColumn: parentRef?.columnId ? undefined : '1/-1',
            }}
          >
            <NavbarDropdownMenuItemElement
              element={elWithoutLink}
              className={`slate-${el.type}`}
              {...elWithoutLink}
            >
              {renderElement(el.children as unknown as NavbarElement)}
            </NavbarDropdownMenuItemElement>
          </DropdownElementBox>
        );
      }

      default:
        return undefined;
    }
  };

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {
        show && (
          <NavbarDropdownSelectedItemProvider>
            <NavbarEditorWrapper
              className="editorWrapper"
            >
              <NavbarDropdownMenuWrapper
                ref={dropdownMenuRef}
                className="typedream content"
              >
                {renderElement(dropdownMenus)}
              </NavbarDropdownMenuWrapper>
            </NavbarEditorWrapper>
          </NavbarDropdownSelectedItemProvider>
        )
      }
    </>
  );
}

NavbarDropdownMenuEditor.defaultProps = {
  show: false,
};

export { NavbarDropdownMenuEditor };
